import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from '../context/RouterContext';
import { PlateService } from '../services/plateService';
import { VoteService } from '../services/voteService';
import { LicensePlate } from '../types';
import EmojiReactions from '../components/EmojiReactions';
import UserService from '../services/userService';

const PlateDetail: React.FC = () => {
  const { plateId, navigate } = useRouter();
  const [plate, setPlate] = useState<LicensePlate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votedPlates, setVotedPlates] = useState<Set<string>>(new Set());


  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const loadPlate = useCallback(async () => {
    if (!plateId) return;

    try {
      setLoading(true);
      setError(null);
      
      const foundPlate = await PlateService.getPlateById(plateId);
      
      if (foundPlate) {
        setPlate(foundPlate);
      } else {
        setError('Plate not found');
      }
    } catch (err) {
      console.error('Error loading plate:', err);
      setError('Failed to load plate');
    } finally {
      setLoading(false);
    }
  }, [plateId]);

  useEffect(() => {
    if (!plateId) {
      setError('No plate ID provided');
      setLoading(false);
      return;
    }

    loadPlate();
  }, [plateId, loadPlate]);

  const handleVote = async (voteType: 'upvote' | 'downvote') => {
    if (!plate || !plateId) return;

    if (VoteService.hasUserVoted(plateId)) {
      return;
    }

    setPlate(prevPlate => {
      if (!prevPlate) return null;
      const newVotes = { ...prevPlate.votes };
      if (voteType === 'upvote') {
        newVotes.upvotes += 1;
        newVotes.total += 1;
      } else {
        newVotes.downvotes += 1;
        newVotes.total -= 1;
      }
      return { ...prevPlate, votes: newVotes };
    });
    
    setVotedPlates(prev => new Set([...prev, plateId]));

    try {
      const result = await VoteService.submitVote(plateId, voteType);
      if (!result.success) loadPlate();
    } catch (error) {
      console.error('Error submitting vote:', error);
      loadPlate();
    }
  };

  const handleEmojiReaction = async (emoji: string) => {
    if (!plate || !plateId) return;

    try {
      const userId = await UserService.getUserId();
      const today = new Date().toISOString().split('T')[0];
      
      setPlate(prevPlate => {
        if (!prevPlate) return prevPlate;
        
        const existingReactionIndex = prevPlate.emojiReactions.findIndex(r => r.emoji === emoji);
        
        if (existingReactionIndex >= 0) {
          const existingReaction = prevPlate.emojiReactions[existingReactionIndex];
          const userReactions = existingReaction.userReactions || {};
          
          if (userReactions[userId] === today) return prevPlate;
          
          const updatedReactions = [...prevPlate.emojiReactions];
          updatedReactions[existingReactionIndex] = {
            ...updatedReactions[existingReactionIndex],
            count: updatedReactions[existingReactionIndex].count + 1,
            users: [...updatedReactions[existingReactionIndex].users, userId],
            userReactions: {
              ...updatedReactions[existingReactionIndex].userReactions,
              [userId]: today
            }
          };
          return { ...prevPlate, emojiReactions: updatedReactions };
        } else {
          return {
            ...prevPlate,
            emojiReactions: [
              ...prevPlate.emojiReactions,
              { emoji, count: 1, users: [userId], userReactions: { [userId]: today } }
            ]
          };
        }
      });
      
      await PlateService.addEmojiReaction(plateId, emoji, userId);
    } catch (error) {
      console.error('Error adding emoji reaction:', error);
      loadPlate();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-200 dark:border-primary-800 border-t-primary-500 dark:border-t-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">Loading plate...</p>
        </div>
      </div>
    );
  }

  if (error || !plate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-3">
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-2xl p-4">
            <svg className="w-12 h-12 text-red-400 dark:text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Plate Not Found</h2>
            <p className="text-gray-600 dark:text-slate-400 mb-4">{error || 'The plate you\'re looking for doesn\'t exist.'}</p>
            <button
              onClick={() => navigate('home')}
              className="bg-primary-500 text-white px-4 py-2 rounded-xl hover:bg-primary-600 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasVoted = plateId ? (votedPlates.has(plateId) || VoteService.hasUserVoted(plateId)) : false;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-shrink-0 px-3 py-3">
        <button
          onClick={() => navigate('home')}
          className="flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors group"
        >
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to All Plates
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* Details - left side on desktop */}
        <div className="flex-shrink-0 lg:w-80 xl:w-96 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-slate-700/50 bg-white dark:bg-slate-800/50 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  License Plate Details
                </h1>
                {plate.state && (
                  <div className="flex items-center text-gray-600 dark:text-slate-400">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm font-medium">{plate.state}</span>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
                className="flex items-center px-3 py-2 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50 rounded-xl transition-colors"
                title="Copy link to share"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleVote('upvote')}
                    disabled={hasVoted}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                      hasVoted
                        ? 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-500/30'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    <span>{plate.votes.upvotes}</span>
                  </button>

                  <button
                    onClick={() => handleVote('downvote')}
                    disabled={hasVoted}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                      hasVoted
                        ? 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <span>{plate.votes.downvotes}</span>
                  </button>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {plate.votes.total > 0 ? '+' : ''}{plate.votes.total}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-slate-400">
                    {plate.votes.upvotes + plate.votes.downvotes} total votes
                  </div>
                </div>
              </div>

              {hasVoted && (
                <div className="mt-3 text-sm text-gray-600 dark:text-slate-400 text-center">
                  <div className="flex items-center justify-center mb-1">
                    <svg className="w-4 h-4 mr-1 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Thank you for voting!
                  </div>
                  {plateId && (() => {
                    const voteData = VoteService.getUserVoteData(plateId);
                    if (voteData) {
                      const voteDate = new Date(voteData.date);
                      const nextVoteDate = new Date(voteDate);
                      nextVoteDate.setDate(nextVoteDate.getDate() + 1);
                      const today = new Date();
                      const canVoteAgain = today >= nextVoteDate;
                      
                      return (
                        <div className="text-xs text-gray-500 dark:text-slate-500">
                          <div>You voted on {voteDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                          {!canVoteAgain && (
                            <div>You can vote again on {nextVoteDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-3 mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Reactions</h3>
              <EmojiReactions
                reactions={plate.emojiReactions || []}
                onReaction={handleEmojiReaction}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-slate-400">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Uploaded {formatDate(plate.uploadDate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Image - right side on desktop, fills remaining space */}
        <div className="flex-1 min-h-[50vh] lg:min-h-0 flex items-center justify-center bg-gray-100 dark:bg-slate-800/50 p-4">
          <img
            src={plate.imageUrl}
            alt={`License plate ${plate.id}`}
            className="max-w-full max-h-[calc(100vh-180px)] lg:max-h-[calc(100vh-120px)] w-auto h-auto object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default PlateDetail;
